<template>
    <div class="chat-distribution-by-handle-chart" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <HalfPieChart
                :title="$t('report.chatDistByHandleType')"
                :sub-title="$t('report.chatDistByHandDesc')"
                :series="getSeries()"
                v-if="!tableMode"
                :colors="['#ec3131', '#f30879', '#f3f308']"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { mapState } from "vuex";
import fscreen from "fscreen";
import HalfPieChart from "../../Pages/Chatbot/pages/reports/charts/HalfPieChart.vue";

const log = log2("ChatDistributionsByHandleType");
export default Vue.extend({
    name: "ChatDistributionsByHandleType",
    components: { HalfPieChart, DoubleBarChart, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
        };
    },
    computed: {
        ...mapState({}),
    },
    methods: {
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".chat-distribution-by-handle-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t('message.type'),
                    dataIndex: "type",
                    key: "type",
                },
                {
                    title: this.$t('message.chatCount'),
                    dataIndex: "chatCount",
                    key: "chatCount",
                },
            ];
        },
        getRows() {
            const data = this.data || { agent: 0, chatbot: 0, hybrid: 0 };
            log("getRows.items", data);
            const rows = [
                {
                    type: this.$t('billing.agent'),
                    chatCount: data.agent,
                },
                {
                    type: "Chatbot",
                    chatCount: data.chatbot,
                },
                {
                    type: this.$t('report.hybrid'),
                    chatCount: data.hybrid,
                },
            ];
            return rows;
        },
        getSeries() {
            let data = this.data || { agent: 0, chatbot: 0, hybrid: 0 };
            log("getSeries.items", data);

            const series = [
                {
                    type: "pie",
                    name: this.$t('message.chatCount'),
                    radius: ["40%", "70%"],
                    center: ["50%", "70%"],
                    // adjust the start and end angle
                    startAngle: 180,
                    endAngle: 360,
                    data: [
                        {
                            value: data.agent,
                            name: this.$t('billing.agent'),
                        },
                        {
                            value: data.chatbot,
                            name: "Chatbot",
                        },
                        {
                            value: data.hybrid,
                            name: this.$t('report.hybrid'),
                        },
                    ],
                },
            ];

            return series;
        },
        exportToExcel() {
            const data = this.data || { agent: 0, chatbot: 0, hybrid: 0 };
            log("exportToExcel.items", data);

            const excelData = [
                {
                    Type: "Agent",
                    "Chat Count": data.agent,
                },
                {
                    Type: "Chatbot",
                    "Chat Count": data.chatbot,
                },
                {
                    Type: "Hybrid",
                    "Chat Count": data.hybrid,
                },
            ];
            expertToExcel(excelData, `chat_counts_by_answer.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".chat-distribution-by-handle-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.chat-distribution-by-handle-chart {
    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.chat-distribution-by-handle-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
